import { FC, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BG_COLORS,
  LANGUAGES,
  RATIOS,
  RESOLUTIONS,
  SAMPLE_AVATARS,
  SAMPLE_WORKS,
  type WorkFilter,
  type WorkStatus,
  isTransparentBg,
  statusText,
} from "./data";
import { AvatarPickerModal, VoicePickerModal, BackgroundPickerModal } from "./pickers";

const InputIcon = ({ name }: { name: "text" | "audio" | "ppt" }) => {
  if (name === "text") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 7V4h16v3" />
        <path d="M9 20h6" />
        <path d="M12 4v16" />
      </svg>
    );
  }
  if (name === "audio") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 14v-3a9 9 0 0 1 18 0v3" />
        <path d="M3 12h4v9H3z" />
        <path d="M17 12h4v9h-4z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="15" y2="17" />
    </svg>
  );
};

const SparkleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m12 3 2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12z" />
    <path d="M20 2v4" />
    <path d="M18 4h4" />
  </svg>
);

const HelpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const WorkCardThumb: FC<{ type: "talking" | "course"; status: WorkStatus; progress?: number }> = ({ type, status, progress }) => {
  const humanColor = status === "failed" ? "#b85c5c" : status === "done" ? "#b85c5c" : "#5a7a96";
  return (
    <div className="adh-thumb">
      <span className={`adh-thumb-badge ${status}`}>{statusText(status)}</span>
      {status === "processing" && progress !== undefined && (
        <span className="adh-thumb-progress">生成中 {progress}%</span>
      )}
      <div className="adh-thumb-stage">
        {type === "course" && (
          <div className="adh-thumb-slide">
            <div className="adh-slide-line" />
            <div className="adh-slide-line short" />
          </div>
        )}
        <div className="adh-thumb-human" style={{ background: humanColor }}>
          <span className="adh-human-head" />
        </div>
      </div>
    </div>
  );
};

export const AiDigitalHumanStudio: FC = () => {
  const navigate = useNavigate();
  const [inputMode, setInputMode] = useState<"text" | "audio">("text");
  const [scriptText, setScriptText] = useState(
    "大家好，欢迎来到本期产品介绍。今天我将用三分钟，带大家了解我们的全新企业级 AI 创作平台。"
  );
  const [selectedAvatar, setSelectedAvatar] = useState("思萌");
  const [selectedVoice, setSelectedVoice] = useState("温柔女声");
  const [selectedBackground, setSelectedBackground] = useState("演播室");
  const [language, setLanguage] = useState("中文");
  const [ratio, setRatio] = useState("16:9");
  const [resolution, setResolution] = useState("1080p");
  const [workFilter, setWorkFilter] = useState<WorkFilter>("all");
  const [audioUploaded, setAudioUploaded] = useState(false);
  const [toast, setToast] = useState("");
  const [modal, setModal] = useState<string | null>(null);

  // Layout composer state
  const [layoutBackground, setLayoutBackground] = useState(selectedBackground);
  const [layoutRatio, setLayoutRatio] = useState(ratio);
  const [subtitleEnabled, setSubtitleEnabled] = useState(true);
  const [humanScale, setHumanScale] = useState(80);
  const [humanPos, setHumanPos] = useState({ x: 50, y: 55 });
  const [subtitlePos, setSubtitlePos] = useState({ x: 50, y: 88 });

  const stageRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    target: "human" | "subtitle";
    startX: number;
    startY: number;
    initX: number;
    initY: number;
  } | null>(null);

  const RATIO_MAP: Record<string, string> = {
    "16:9": "16 / 9",
    "9:16": "9 / 16",
    "4:3": "4 / 3",
    "21:9": "21 / 9",
  };

  const selectedAvatarColor = useMemo(
    () => SAMPLE_AVATARS.find((a) => a.name === selectedAvatar)?.color || "#b85c5c",
    [selectedAvatar]
  );

  const startDrag = (target: "human" | "subtitle", e: React.PointerEvent) => {
    e.preventDefault();
    const pos = target === "human" ? humanPos : subtitlePos;
    dragRef.current = {
      target,
      startX: e.clientX,
      startY: e.clientY,
      initX: pos.x,
      initY: pos.y,
    };
    const move = (ev: PointerEvent) => {
      if (!dragRef.current || !stageRef.current) return;
      const rect = stageRef.current.getBoundingClientRect();
      const dx = ((ev.clientX - dragRef.current.startX) / rect.width) * 100;
      const dy = ((ev.clientY - dragRef.current.startY) / rect.height) * 100;
      const x = Math.max(5, Math.min(95, dragRef.current.initX + dx));
      const y = Math.max(5, Math.min(95, dragRef.current.initY + dy));
      if (dragRef.current.target === "human") setHumanPos({ x, y });
      else setSubtitlePos({ x, y });
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const align = (position: "left" | "center" | "right") => {
    const x = position === "left" ? 20 : position === "right" ? 80 : 50;
    setHumanPos((p) => ({ ...p, x }));
    setSubtitlePos((p) => ({ ...p, x }));
  };

  const resetLayout = () => {
    setHumanPos({ x: 50, y: 55 });
    setSubtitlePos({ x: 50, y: 88 });
    setHumanScale(80);
  };

  const openLayout = () => {
    setLayoutBackground(selectedBackground);
    setLayoutRatio(ratio);
    resetLayout();
    openModal("layout");
  };

  const confirmLayout = () => {
    setSelectedBackground(layoutBackground);
    setRatio(layoutRatio);
    closeModal();
    showToast("已确认画面");
  };

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2600);
  };

  const worksFiltered = useMemo(
    () => (workFilter === "all" ? SAMPLE_WORKS : SAMPLE_WORKS.filter((w) => w.type === workFilter)),
    [workFilter]
  );

  const openModal = (id: string) => setModal(id);
  const closeModal = () => setModal(null);

  return (
    <div className="adh-page">
      <header className="adh-hero">
        <span className="adh-kicker"><i />Digital Human Studio</span>
        <h1>AI 数字人</h1>
        <p>通过文案、音频或课件，创建口播视频与视频课。</p>
      </header>

      <section className="adh-card adh-composer">
        <div className="adh-input-tabs" role="tablist">
          <button className={inputMode === "text" ? "active" : ""} type="button" onClick={() => setInputMode("text")}>
            <InputIcon name="text" />文案输入<small>口播视频</small>
          </button>
          <button className={inputMode === "audio" ? "active" : ""} type="button" onClick={() => setInputMode("audio")}>
            <InputIcon name="audio" />音频上传<small>口播视频</small>
          </button>
          <button className="disabled" type="button" disabled title="PPT 文档输入暂不可用">
            <InputIcon name="ppt" />PPT 文档<small>视频课</small>
          </button>
        </div>

        {inputMode === "text" && (
          <div className="adh-script-area">
            <div className="adh-field-head">
              <label>口播文案</label>
              <button className="adh-ai-link" type="button" onClick={() => openModal("writing")}>
                <SparkleIcon /> AI 帮我写
              </button>
            </div>
            <textarea
              value={scriptText}
              onChange={(e) => setScriptText(e.target.value)}
              maxLength={5000}
              placeholder="请输入口播文案，也可以点击「AI 帮我写」生成文稿。"
            />
            <span className="adh-counter">{scriptText.length} / 5,000</span>
          </div>
        )}

        {inputMode === "audio" && (
          <div className="adh-audio-area">
            {!audioUploaded ? (
              <button className="adh-upload-zone" type="button" onClick={() => setAudioUploaded(true)}>
                <span className="adh-upload-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 18H6a4 4 0 0 1-1-8 7 7 0 0 1 13-3 5 5 0 0 1 1 10" />
                    <path d="M12 21V10" />
                    <path d="m8 14 4-4 4 4" />
                  </svg>
                </span>
                <strong>点击上传或拖拽音频文件到此处</strong>
                <small>MP3、WAV、M4A · ≤ 100MB</small>
              </button>
            ) : (
              <div className="adh-file-card">
                <span className="adh-file-icon">▶</span>
                <div>
                  <strong>企业产品介绍.wav</strong>
                  <small>12.6 MB · 02:34 · WAV</small>
                </div>
                <button type="button" aria-label="删除" onClick={() => setAudioUploaded(false)}>×</button>
              </div>
            )}
          </div>
        )}

        <div className="adh-settings">
          <div className="adh-settings-head">
            <strong>视频设置</strong>
            <button className="adh-text-btn" type="button" onClick={openLayout}>调整画面 →</button>
          </div>
          <div className="adh-settings-grid">
            <div className="adh-setting-cell">
              <label>数字人</label>
              <button type="button" className="adh-select-btn" onClick={() => openModal("picker-avatar")}>
                {selectedAvatar}
                <svg viewBox="0 0 24 24"><path d="m9 18 6-6-6-6" /></svg>
              </button>
            </div>
            <div className="adh-setting-cell">
              <label>声音</label>
              <button type="button" className="adh-select-btn" onClick={() => openModal("picker-voice")}>
                {selectedVoice}
                <svg viewBox="0 0 24 24"><path d="m9 18 6-6-6-6" /></svg>
              </button>
            </div>
            <div className="adh-setting-cell">
              <label>视频背景</label>
              <button type="button" className="adh-select-btn" onClick={() => openModal("background")}>
                {selectedBackground}
                <svg viewBox="0 0 24 24"><path d="m9 18 6-6-6-6" /></svg>
              </button>
            </div>
            <div className="adh-setting-cell">
              <label>合成语言</label>
              <div className="adh-select-wrap">
                <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                  {LANGUAGES.map((l) => (<option key={l}>{l}</option>))}
                </select>
                <svg viewBox="0 0 24 24"><path d="m6 9 6 6 6-6" /></svg>
              </div>
            </div>
            <div className="adh-setting-cell">
              <label>视频比例</label>
              <div className="adh-select-wrap">
                <select value={ratio} onChange={(e) => setRatio(e.target.value)}>
                  {RATIOS.map((r) => (<option key={r}>{r}</option>))}
                </select>
                <svg viewBox="0 0 24 24"><path d="m6 9 6 6 6-6" /></svg>
              </div>
            </div>
            <div className="adh-setting-cell">
              <label>分辨率</label>
              <div className="adh-select-wrap">
                <select value={resolution} onChange={(e) => setResolution(e.target.value)}>
                  {RESOLUTIONS.map((r) => (<option key={r}>{r}</option>))}
                </select>
                <svg viewBox="0 0 24 24"><path d="m6 9 6 6 6-6" /></svg>
              </div>
            </div>
          </div>
          <p className="adh-note">按原文合成，不自动翻译；请使用与所选语言一致的文案。</p>
        </div>

        <div className="adh-composer-footer">
          <span className="adh-cost">预计消耗 <b>24</b> 积分</span>
          <button className="adh-primary-btn" type="button" onClick={() => showToast("已提交生成任务（原型演示）")}>
            生成口播视频 <span>→</span>
          </button>
        </div>
      </section>

      <section className="adh-section">
        <div className="adh-section-head">
          <h2>我的作品</h2>
          <div className="adh-section-links">
            <button type="button" onClick={() => navigate("/hub/ai-digital-human/avatars")}>我的数字人</button>
            <button type="button" onClick={() => navigate("/hub/ai-digital-human/templates")}>我的模板</button>
          </div>
        </div>
        <div className="adh-filter-tabs">
          <button className={workFilter === "all" ? "active" : ""} type="button" onClick={() => setWorkFilter("all")}>
            全部 <span>{SAMPLE_WORKS.length}</span>
          </button>
          <button className={workFilter === "talking" ? "active" : ""} type="button" onClick={() => setWorkFilter("talking")}>
            口播视频 <span>{SAMPLE_WORKS.filter((w) => w.type === "talking").length}</span>
          </button>
          <button className={workFilter === "course" ? "active" : ""} type="button" onClick={() => setWorkFilter("course")}>
            视频课 <span>{SAMPLE_WORKS.filter((w) => w.type === "course").length}</span>
          </button>
        </div>
        <div className="adh-work-grid">
          {worksFiltered.map((w) => (
            <article key={w.id} className="adh-work-card">
              <WorkCardThumb type={w.type} status={w.status} progress={w.progress} />
              <div className="adh-work-meta">
                <strong>{w.title} <i>✎</i></strong>
                <small>{w.date} · {w.duration} · {w.ratio}</small>
              </div>
            </article>
          ))}
        </div>
      </section>

      {modal === "writing" && (
        <div className="adh-modal-backdrop" onClick={closeModal}>
          <div className="adh-modal adh-modal--md" onClick={(e) => e.stopPropagation()}>
            <header>
              <h3>AI 帮我写</h3>
              <button type="button" className="adh-modal-close" onClick={closeModal}>×</button>
            </header>
            <div className="adh-modal-body">
              <label className="adh-form-field">
                <span>口播主题</span>
                <input placeholder="例如：向新员工介绍公司的报销流程" />
              </label>
              <div className="adh-form-row">
                <label className="adh-form-field">
                  <span>目标时长</span>
                  <select defaultValue="60">
                    <option value="30">约 30 秒</option>
                    <option value="60">约 1 分钟</option>
                    <option value="180">约 3 分钟</option>
                  </select>
                </label>
                <button className="adh-secondary-btn" type="button" onClick={() => showToast("已生成新稿")}>生成新稿</button>
              </div>
              <label className="adh-form-field">
                <span>新稿预览 · 可继续编辑</span>
                <textarea defaultValue="这是一份由 AI 生成的示例口播文稿，原型演示未调用真实模型。" />
              </label>
            </div>
            <footer>
              <button className="adh-secondary-btn" type="button" onClick={closeModal}>取消</button>
              <button className="adh-primary-btn" type="button" onClick={() => { setScriptText("这是一份由 AI 生成的示例口播文稿。"); closeModal(); showToast("已填入口播文案"); }}>使用这篇文稿</button>
            </footer>
          </div>
        </div>
      )}

      {modal === "picker-avatar" && (
        <AvatarPickerModal
          value={selectedAvatar}
          onClose={closeModal}
          onConfirm={(name) => { setSelectedAvatar(name); closeModal(); showToast("已选择 " + name); }}
        />
      )}

      {modal === "picker-voice" && (
        <VoicePickerModal
          value={selectedVoice}
          onClose={closeModal}
          onConfirm={(name) => { setSelectedVoice(name); closeModal(); showToast("已选择 " + name); }}
        />
      )}

      {modal === "background" && (
        <BackgroundPickerModal
          value={selectedBackground}
          onClose={closeModal}
          onConfirm={(bg) => { setSelectedBackground(bg); closeModal(); showToast("已使用背景 " + bg); }}
        />
      )}

      {modal === "layout-background" && (
        <BackgroundPickerModal
          value={layoutBackground}
          onClose={() => setModal("layout")}
          onConfirm={(bg) => { setLayoutBackground(bg); setModal("layout"); }}
        />
      )}

      {modal === "layout" && (
        <div className="adh-modal-backdrop" onClick={closeModal}>
          <div className="adh-modal adh-modal--xl adh-layout-modal" onClick={(e) => e.stopPropagation()}>
            <header>
              <h3>调整口播画面</h3>
              <p>拖动调整位置，拖动右下角或用滑杆等比缩放。</p>
              <button type="button" className="adh-modal-close" onClick={closeModal}>×</button>
            </header>
            <div className="adh-modal-body">
              {/* 第一行：视频背景 + 视频比例 */}
              <div className="adh-layout-toolbar adh-layout-toolbar--top">
                <button
                  type="button"
                  className="adh-layout-bg-trigger"
                  onClick={() => setModal("layout-background")}
                >
                  <span className="adh-layout-bg-thumb" style={{ background: BG_COLORS[layoutBackground] }} />
                  <span className="adh-layout-bg-text">
                    <small>背景</small>
                    <strong>{layoutBackground}</strong>
                  </span>
                  <svg className="adh-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>

                <div className="adh-layout-field adh-layout-field--ratio">
                  <label>视频比例</label>
                  <div className="adh-select-wrap">
                    <select value={layoutRatio} onChange={(e) => setLayoutRatio(e.target.value)}>
                      {RATIOS.map((r) => (<option key={r}>{r}</option>))}
                    </select>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* 第二行：字幕开关 */}
              <div className="adh-layout-subtitle-row">
                <label>字幕</label>
                <button
                  type="button"
                  className={`adh-switch ${subtitleEnabled ? "on" : ""}`}
                  onClick={() => setSubtitleEnabled((v) => !v)}
                  aria-checked={subtitleEnabled}
                  role="switch"
                >
                  <i />
                </button>
              </div>

              {/* 第三行：画布（外围空白区 #f2f4f7） */}
              <div className="adh-layout-stage-area">
                <div
                  className={`adh-layout-stage${isTransparentBg(layoutBackground) ? " is-transparent" : ""}`}
                  ref={stageRef}
                  style={{ aspectRatio: RATIO_MAP[layoutRatio], background: isTransparentBg(layoutBackground) ? undefined : BG_COLORS[layoutBackground] }}
                >
                  <div
                    className="adh-layout-human"
                    style={{
                      left: `${humanPos.x}%`,
                      top: `${humanPos.y}%`,
                      transform: `translate(-50%, -50%) scale(${humanScale / 100})`,
                      background: selectedAvatarColor,
                    }}
                    onPointerDown={(e) => startDrag("human", e)}
                  >
                    <span className="adh-layout-human-label">思萌 · 透明形象示意</span>
                    <span />
                  </div>
                  {subtitleEnabled && (
                    <div
                      className="adh-layout-subtitle"
                      style={{ left: `${subtitlePos.x}%`, top: `${subtitlePos.y}%` }}
                      onPointerDown={(e) => startDrag("subtitle", e)}
                    >
                      大家好，欢迎来到本期产品介绍。今天我将用三分钟，带大家了解我们的全新企业级…
                    </div>
                  )}
                </div>
              </div>
            </div>

            <footer className="adh-layout-footer">
              {/* 第四行：居左/居中/居右/重置 + 大小缩放 */}
              <div className="adh-layout-footer__row adh-layout-footer__row--actions">
                <div className="adh-layout-actions-left">
                  <button type="button" onClick={() => align("left")}>居左</button>
                  <button type="button" onClick={() => align("center")}>居中</button>
                  <button type="button" onClick={() => align("right")}>居右</button>
                  <button type="button" onClick={resetLayout}>重置</button>
                </div>
                <div className="adh-layout-scale">
                  <label>大小</label>
                  <input
                    type="range"
                    min={20}
                    max={120}
                    value={humanScale}
                    onChange={(e) => setHumanScale(Number(e.target.value))}
                  />
                  <span>{humanScale}%</span>
                </div>
              </div>

              {/* 第五行：提示 + 取消/确认 */}
              <div className="adh-layout-footer__row adh-layout-footer__row--confirm">
                <div className="adh-layout-hint">
                  仅调整当前口播构图，不改变文稿和音频。
                  <HelpIcon />
                </div>
                <div className="adh-layout-actions-right">
                  <button className="adh-secondary-btn" type="button" onClick={closeModal}>取消</button>
                  <button className="adh-primary-btn" type="button" onClick={confirmLayout}>确认画面</button>
                </div>
              </div>
            </footer>
          </div>
        </div>
      )}

      {toast && <div className="adh-toast" role="status">{toast}</div>}
    </div>
  );
};
